/**
 * The provisional rules that owe a mandatory re-vote, AS DOCKET ITEMS.
 *
 * THE BUG THIS EXISTS TO FIX. Two 2025 rules - the two-round farm draft and the keeper apron -
 * passed conditionally and must be re-voted in 2026 or they lapse (2019-I-2). The Proposals screen
 * listed them, correctly, in a card of their own. But they were rulebook ENTRIES, not proposals,
 * so they were never in `docket.order`; Tim published the 2026 docket on 25 August and got one
 * item where he expected three. Nothing was broken enough to notice - the agenda simply did not
 * contain the two things nobody had to remember to add, which is exactly the failure the card was
 * written to prevent.
 *
 * Left alone it would not have surfaced as a bug at all. It would have surfaced as two rules
 * quietly lapsing on 26 August because they never reached a vote, which is the same outcome as
 * the committee rejecting them and none of the deliberation.
 *
 * WHY THIS IS A PROPOSAL AND NOT A SPECIAL CASE. Everything the meeting needs already exists for
 * proposals and only for proposals: `isRevote` is on `RuleProposal`, `tallyOutcome` reads it to
 * refuse a six-vote pass the second time round, `DocketOrder` draws the re-vote chip from it, and
 * `commitPlan` moves a failed re-vote out of force. A second kind of agenda item would need all
 * four taught about it. So a carryover becomes an ordinary docket item that happens to carry a
 * flag - which is also what `rollover.ts` does at season end, and this deliberately copies its id
 * convention so that a rollover which later runs over the same season finds these already here
 * rather than making a second set.
 */

import { revoteTarget, revoteWording } from './revote.js';
import type { Override } from './overrides.js';
import type { RulebookEntry } from './rulebook.js';
import type { RuleProposal } from '../store/types.js';

/** What `rulebook.json` marks a 2025 conditional pass with. */
export const REVOTE_OUTCOME = 'conditional-pass-revote-2026';

/**
 * The rules that lapse this season unless the committee re-votes them through.
 *
 * MATCHED ON `provisional`, NOT ON THE 2025 MARKER. It used to also require
 * `outcome === REVOTE_OUTCOME`, and that constant is the literal string
 * `conditional-pass-revote-2026` — so the predicate only ever found conditional passes from 2025.
 * A rule passed on the relaxed six in 2026 carries `conditional-pass-revote-2027` and would never
 * have been flagged for the re-vote it is owed, which is the one thing this function exists to
 * prevent. Found on 26 August by `ruleDecisions.test.ts`, which enacts one and asks for it back.
 *
 * `status === 'active' && provisional` is the whole of 2019-I-2 §2 anyway: in force, and owing a
 * re-vote. The outcome string is a label on top of that, not the fact itself.
 */
export function carryoverRules(rules: readonly RulebookEntry[]): RulebookEntry[] {
  return rules.filter((r) => r.status === 'active' && r.provisional === true);
}

/** `rollover.ts`'s convention, on purpose - see the note above. */
export const carryoverId = (season: number, ruleId: string) => `revote-${season}-${ruleId}`;

/**
 * The proposer's GM id, resolved against the roster rather than assumed.
 *
 * `splitRuleTitle` reads a NAME out of a generated title - "Rajan" - and the veto in 2020-I-1 §2
 * hangs off `gm`, so guessing wrong here would hand one man's veto to another or to nobody. An
 * unrecognised name is kept as written rather than dropped: a proposal with a strange author is
 * visible and fixable, one with no author silently loses the veto.
 */
export function proposerId(name: string | undefined, known: readonly string[]): string {
  const up = (name ?? '').trim().toUpperCase();
  return known.find((g) => g.toUpperCase() === up) ?? up;
}

/**
 * One carryover, as it should appear on the docket.
 *
 * It takes the OVERRIDES so that a commissioner's tidy-up of the wording travels onto the agenda.
 * That is the whole point of being able to edit these: the room reads the docket, not the
 * rulebook, and an edit that stopped at the re-vote card would be an edit nobody sees.
 *
 * `docketed`, not `submitted`: 2015-I-1 puts it on the agenda automatically, so making somebody
 * re-submit it would be the app inventing a step the rule does not have.
 */
export function carryoverProposal(
  rule: RulebookEntry,
  overrides: readonly Override[],
  season: number,
  now: string,
  known: readonly string[],
): RuleProposal {
  const w = revoteWording(rule, overrides);
  return {
    id: carryoverId(season, rule.id),
    season,
    gm: proposerId(w.proposer, known),
    title: w.title,
    body: w.summary,
    amends: [rule.id],
    status: 'docketed',
    isRevote: true,
    createdAt: now,
    updatedAt: now,
  } as RuleProposal;
}

/**
 * The carryovers that are not on the docket yet.
 *
 * Keyed on the id rather than on the title, so a commissioner renaming one does not make the app
 * think it is missing and add a second copy of it.
 */
export function missingCarryovers(
  rules: readonly RulebookEntry[],
  proposals: readonly { id: string }[],
  season: number,
): RulebookEntry[] {
  const have = new Set(proposals.map((p) => p.id));
  return carryoverRules(rules).filter((r) => !have.has(carryoverId(season, r.id)));
}

/** Ids to append to `docket.order`, skipping any already in it. */
export function appendToOrder(order: readonly string[], ids: readonly string[]): string[] {
  return [...order, ...ids.filter((id) => !order.includes(id))];
}

/** Untouched by `revoteTarget`, but re-exported so callers need one import for this subject. */
export { revoteTarget };
